import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from '@/firebaseConfig'
import i18n from '@/i18n'

const LOCALE_KEY = 'locale'
export const DEFAULT_LOCALE = 'da'

export function setStoredLocale(locale: string) {
  try {
    localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    /* ignore */
  }
}

export function getStoredLocale(): string {
  try {
    return localStorage.getItem(LOCALE_KEY) || DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

/** Central place to change the effective locale for the app. */
export function setI18nLocale(locale: string) {
  const locales = i18n.global.availableLocales as unknown as string[]
  const valid: string = locales.includes(locale) ? locale : 'en'
  ;(i18n.global.locale as any).value = valid
  setStoredLocale(valid)
}

/**
 * Persists the selected language on the user's Firestore document so the
 * preference follows the user across devices. No-op when logged out.
 */
export async function saveUserLocale(locale: string, uid?: string | null) {
  const id = uid ?? auth.currentUser?.uid
  if (!id) return
  try {
    await updateDoc(doc(db, 'users', id), { locale })
  } catch (e) {
    console.error('Failed to save locale to user document:', e)
  }
}

/**
 * Loads the stored language from the user's Firestore document on
 * authentication and applies it to i18n (and localStorage as a cache).
 */
export async function syncLocaleFromUser(uid: string | null, currentLocale: string) {
  if (!uid) return
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    const data = snap.data()
    const remote = data && typeof data.locale === 'string' ? data.locale : null
    if (remote && remote !== currentLocale) {
      setI18nLocale(remote)
    }
  } catch (e) {
    console.error('Failed to load locale from user document:', e)
  }
}

/**
 * Composable for language switchers (header, profile, mobile nav).
 * `select(key)` receives the 0-based index into `availableLocales`,
 * matching PrimeVue SelectButton usage with `optionValue="key"`.
 */
export function useLanguage() {
  const { locale, availableLocales } = useI18n()

  const selectedLanguage = computed(() => {
    const idx = availableLocales.indexOf(locale.value)
    return idx >= 0 ? idx : 0
  })

  const changeLanguage = (key: number) => {
    const next = availableLocales[key]
    if (!next || next === locale.value) return
    setI18nLocale(next)
    saveUserLocale(next)
  }

  return { locale, availableLocales, selectedLanguage, changeLanguage }
}
